import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Button,
  Table,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
  Form,
  Pagination,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaDownload,
  FaEye,
  FaChartLine,
  FaCheckCircle,
  FaTimesCircle,
  FaSearch,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import TestService from "../../../services/testService";

const TestResults = () => {
  const { testId } = useParams();
  const navigate = useNavigate();

  const [test, setTest] = useState(null);
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentDetails, setShowStudentDetails] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });

  useEffect(() => {
    loadTestAndResults();
  }, [testId]);

  useEffect(() => {
    filterResults();
  }, [searchTerm, results]);

  const loadTestAndResults = async () => {
    try {
      setLoading(true);

      // Load test details
      const testResponse = await TestService.getTest(testId);
      setTest(testResponse.data);

      // Load results
      const resultsResponse = await TestService.getTestResults(testId);
      const resultsData = resultsResponse.data || [];
      setResults(resultsData);
      setFilteredResults(resultsData);
      setPagination((prev) => ({
        ...prev,
        total: resultsData.length,
        total_pages: Math.ceil(resultsData.length / prev.per_page),
      }));
    } catch (error) {
      console.error("Failed to load results:", error);
      toast.error("Failed to load test results");
      navigate("/admin/tests");
    } finally {
      setLoading(false);
    }
  };

  const filterResults = () => {
    if (!searchTerm.trim()) {
      setFilteredResults(results);
      setPagination((prev) => ({
        ...prev,
        total: results.length,
        total_pages: Math.ceil(results.length / prev.per_page),
      }));
      return;
    }

    const filtered = results.filter(
      (result) =>
        result.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.student_email?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredResults(filtered);
    setPagination((prev) => ({
      ...prev,
      total: filtered.length,
      total_pages: Math.ceil(filtered.length / prev.per_page),
      current_page: 1,
    }));
  };

  const getPaginatedResults = () => {
    const start = (pagination.current_page - 1) * pagination.per_page;
    const end = start + pagination.per_page;
    return filteredResults.slice(start, end);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination((prev) => ({
      ...prev,
      per_page: newLimit,
      current_page: 1,
      total_pages: Math.ceil(prev.total / newLimit),
    }));
  };

  const viewStudentDetails = async (result) => {
    try {
      // Use admin endpoint instead of student endpoint
      const response = await TestService.getAdminAttemptDetails(result.id);

      if (response.data) {
        setSelectedStudent({
          ...result,
          details: response.data,
        });
        setShowStudentDetails(true);
      } else {
        toast.error("Failed to load student details");
      }
    } catch (error) {
      console.error("Failed to load student details:", error);
      console.error("Error response:", error.response);

      const errorMessage =
        error.response?.data?.message || "Failed to load student details";
      toast.error(errorMessage);
    }
  };

  const exportResults = () => {
    // Create CSV data
    const headers = [
      "Student Name",
      "Email",
      "Score",
      "Percentage",
      "Result",
      "Started At",
      "Completed At",
    ];
    const csvData = filteredResults.map((result) => [
      result.student_name,
      result.student_email,
      `${result.score}/${result.total_possible || 100}`,
      `${result.percentage}%`,
      result.passed ? "Passed" : "Failed",
      new Date(result.started_at).toLocaleString(),
      result.completed_at
        ? new Date(result.completed_at).toLocaleString()
        : "-",
    ]);

    const csvContent = [headers, ...csvData]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${test?.title}_results.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Results exported successfully");
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return "success";
    if (percentage >= 60) return "warning";
    return "danger";
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;
    if (total_pages <= 1) return null;

    let items = [];
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />,
    );

    let startPage = Math.max(1, current_page - 2);
    let endPage = Math.min(total_pages, startPage + 4);

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis1" />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current_page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    if (endPage < total_pages) {
      if (endPage < total_pages - 1)
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      items.push(
        <Pagination.Item
          key={total_pages}
          onClick={() => handlePageChange(total_pages)}
        >
          {total_pages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />,
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <Form.Select
            size="sm"
            value={pagination.per_page}
            onChange={handleItemsPerPageChange}
            style={{ width: "auto" }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
        </div>
        <div className="text-muted small">
          Showing {(pagination.current_page - 1) * pagination.per_page + 1} -{" "}
          {Math.min(
            pagination.current_page * pagination.per_page,
            pagination.total,
          )}{" "}
          of {pagination.total} results
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button
              variant="link"
              className="text-decoration-none mb-2 p-0"
              onClick={() => navigate("/admin/tests")}
            >
              <FaArrowLeft className="me-2" /> Back to Tests
            </Button>
            <h3>
              <FaChartLine className="me-2 text-primary" />
              Test Results: {test?.title}
            </h3>
            <p className="text-muted mb-0">
              Category: {test?.category_name} | Passing:{" "}
              {test?.passing_percentage}% | Total Attempts: {results.length}
            </p>
          </div>
          <Button
            variant="success"
            onClick={exportResults}
            disabled={filteredResults.length === 0}
          >
            <FaDownload className="me-2" /> Export CSV
          </Button>
        </div>

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="shadow-sm text-center">
              <Card.Body>
                <h5 className="text-muted mb-2">Total Attempts</h5>
                <h2 className="mb-0 text-primary">{results.length}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm text-center">
              <Card.Body>
                <h5 className="text-muted mb-2">Passed</h5>
                <h2 className="mb-0 text-success">
                  {results.filter((r) => r.passed).length}
                </h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm text-center">
              <Card.Body>
                <h5 className="text-muted mb-2">Failed</h5>
                <h2 className="mb-0 text-danger">
                  {results.filter((r) => !r.passed).length}
                </h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm text-center">
              <Card.Body>
                <h5 className="text-muted mb-2">Pass Rate</h5>
                <h2 className="mb-0 text-info">
                  {results.length > 0
                    ? Math.round(
                        (results.filter((r) => r.passed).length /
                          results.length) *
                          100,
                      )
                    : 0}
                  %
                </h2>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Results Table */}
        <Card className="shadow-sm">
          <Card.Body>
            {/* Search Bar */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div style={{ width: "300px" }}>
                  <Form.Control
                    placeholder="Search by student name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="text-muted">
                  <small>{filteredResults.length} results found</small>
                </div>
              </div>
            </div>

            {filteredResults.length === 0 ? (
              <Alert variant="info" className="text-center">
                {searchTerm
                  ? "No results match your search."
                  : "No attempts yet for this test."}
              </Alert>
            ) : (
              <>
                <Table striped hover responsive className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>#</th>
                      <th>Student Name</th>
                      <th>Email</th>
                      <th>Score</th>
                      <th>Percentage</th>
                      <th>Result</th>
                      <th>Started At</th>
                      <th>Completed At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedResults().map((result, index) => (
                      <tr key={result.id}>
                        <td>
                          {(pagination.current_page - 1) * pagination.per_page +
                            index +
                            1}
                        </td>
                        <td>
                          <strong>{result.student_name}</strong>
                        </td>
                        <td>{result.student_email}</td>
                        <td>
                          <Badge bg={getScoreColor(result.percentage)}>
                            {result.score || 0}
                          </Badge>
                        </td>
                        <td
                          className={
                            result.passed
                              ? "text-success fw-bold"
                              : "text-danger fw-bold"
                          }
                        >
                          {result.percentage || 0}%
                        </td>
                        <td>
                          {result.passed ? (
                            <Badge bg="success">
                              <FaCheckCircle className="me-1" /> Passed
                            </Badge>
                          ) : (
                            <Badge bg="danger">
                              <FaTimesCircle className="me-1" /> Failed
                            </Badge>
                          )}
                        </td>
                        <td className="small">
                          {result.started_at
                            ? new Date(result.started_at).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="small">
                          {result.completed_at
                            ? new Date(result.completed_at).toLocaleDateString()
                            : "-"}
                        </td>
                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => viewStudentDetails(result)}
                            title="View Details"
                          >
                            <FaEye /> Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {renderPagination()}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Student Details Modal - FIXED VERSION */}
        {selectedStudent && selectedStudent.details && (
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{
              backgroundColor: "rgba(0,0,0,0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050,
              overflow: "auto",
            }}
            onClick={(e) => {
              // Close when clicking backdrop
              if (e.target === e.currentTarget) {
                setShowStudentDetails(false);
                setSelectedStudent(null);
              }
            }}
            onKeyDown={(e) => {
              // Close on ESC key
              if (e.key === "Escape") {
                setShowStudentDetails(false);
                setSelectedStudent(null);
              }
            }}
          >
            <div
              className="modal-dialog modal-lg modal-dialog-centered"
              style={{ margin: "1.75rem auto" }}
            >
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title text-white">
                    Student Details: {selectedStudent.student_name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShowStudentDetails(false);
                      setSelectedStudent(null);
                    }}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Email:</strong> {selectedStudent.student_email}
                    </Col>
                    <Col md={6}>
                      <strong>Test:</strong>{" "}
                      {selectedStudent.details.test_title}
                    </Col>
                    <Col md={6}>
                      <strong>Score:</strong>{" "}
                      {selectedStudent.details.score || 0}/
                      {selectedStudent.total_possible || 100}
                    </Col>
                    <Col md={6}>
                      <strong>Percentage:</strong>{" "}
                      {selectedStudent.details.percentage || 0}%
                    </Col>
                    <Col md={6}>
                      <strong>Passing Percentage:</strong>{" "}
                      {selectedStudent.details.passing_percentage || 60}%
                    </Col>
                    <Col md={6}>
                      <strong>Result:</strong>{" "}
                      {selectedStudent.details.passed ? (
                        <Badge bg="success">✓ Passed</Badge>
                      ) : (
                        <Badge bg="danger">✗ Failed</Badge>
                      )}
                    </Col>
                    <Col md={6}>
                      <strong>Started At:</strong>{" "}
                      {selectedStudent.details.started_at
                        ? new Date(
                            selectedStudent.details.started_at,
                          ).toLocaleString()
                        : "-"}
                    </Col>
                    <Col md={6}>
                      <strong>Completed At:</strong>{" "}
                      {selectedStudent.details.completed_at
                        ? new Date(
                            selectedStudent.details.completed_at,
                          ).toLocaleString()
                        : "-"}
                    </Col>
                  </Row>

                  {selectedStudent.details.answers &&
                    Object.keys(selectedStudent.details.answers).length > 0 && (
                      <>
                        <hr />
                        <h6 className="mb-3">Question-wise Breakdown</h6>
                        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                          {selectedStudent.details.selected_questions &&
                            selectedStudent.details.selected_questions.map(
                              (question, idx) => {
                                const answer =
                                  selectedStudent.details.answers[question.id];
                                return (
                                  <Card
                                    key={question.id}
                                    className={`mb-2 ${answer?.is_correct ? "border-success" : "border-danger"}`}
                                  >
                                    <Card.Body className="py-2">
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                          <strong>
                                            Q{idx + 1}. {question.question}
                                          </strong>
                                        </div>
                                        <Badge
                                          bg={
                                            answer?.is_correct
                                              ? "success"
                                              : "danger"
                                          }
                                          className="ms-2"
                                        >
                                          {answer?.is_correct
                                            ? `${question.marks || 1} marks`
                                            : "0 marks"}
                                        </Badge>
                                      </div>
                                      <div className="mt-2 small">
                                        <Row>
                                          <Col md={6}>
                                            <span className="text-muted">
                                              Your Answer:
                                            </span>{" "}
                                            <strong
                                              className={
                                                answer?.is_correct
                                                  ? "text-success"
                                                  : "text-danger"
                                              }
                                            >
                                              {answer?.selected
                                                ? answer.selected.toUpperCase()
                                                : "Not answered"}
                                              {answer?.selected && (
                                                <span className="ms-2 text-muted">
                                                  (
                                                  {answer.selected === "a"
                                                    ? question.option_a
                                                    : answer.selected === "b"
                                                      ? question.option_b
                                                      : answer.selected === "c"
                                                        ? question.option_c
                                                        : answer.selected ===
                                                            "d"
                                                          ? question.option_d
                                                          : "N/A"}
                                                  )
                                                </span>
                                              )}
                                            </strong>
                                          </Col>
                                          <Col md={6}>
                                            <span className="text-muted">
                                              Correct Answer:
                                            </span>{" "}
                                            <strong className="text-success">
                                              {question.correct_option?.toUpperCase()}
                                              <span className="ms-2 text-muted">
                                                (
                                                {question.correct_option === "a"
                                                  ? question.option_a
                                                  : question.correct_option ===
                                                      "b"
                                                    ? question.option_b
                                                    : question.correct_option ===
                                                        "c"
                                                      ? question.option_c
                                                      : question.correct_option ===
                                                          "d"
                                                        ? question.option_d
                                                        : "N/A"}
                                                )
                                              </span>
                                            </strong>
                                          </Col>
                                        </Row>
                                      </div>
                                    </Card.Body>
                                  </Card>
                                );
                              },
                            )}
                        </div>
                      </>
                    )}
                </div>
                <div className="modal-footer">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowStudentDetails(false);
                      setSelectedStudent(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestResults;
